import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import { formatPrice } from '../utils/priceFormatter';
import { useSponsor } from '../contexts/SponsorContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePositionContext } from '../contexts/PositionContext';
import { usePriceInput } from '../contexts/PriceInputContext';
import { useFullChartView } from '../contexts/FullChartViewContext';
import { useDropdownContext } from '../contexts/DropdownContext';
import MarketTicker from './MarketTicker';

interface LivePriceRightProps {
  selectedSymbol: string;
  currentPrice: string;
  priceChange: string;
  isPricePositive: boolean;
  priceColor: string;
  isLeftPanelVisible?: boolean;
  onSymbolChange?: (symbol: string) => void;
}

const LivePriceRight: React.FC<LivePriceRightProps> = ({
  selectedSymbol,
  currentPrice,
  priceChange,
  isPricePositive,
  priceColor,
  isLeftPanelVisible = true,
  onSymbolChange,
}) => {
  const { isSponsorEnabled } = useSponsor();
  const { theme } = useTheme();
  const { openPositions, totalUnrealizedPnL } = usePositionContext();
  const { fillBuyPrice, fillSellPrice } = usePriceInput();
  const { isFullChartView } = useFullChartView();
  const { isOpen, toggleDropdown } = useDropdownContext();
  
  const [selectedMarket, setSelectedMarket] = useState('Crypto');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const handleSymbolSelect = (symbol: string) => {
    onSymbolChange?.(symbol);
    toggleDropdown('marketTickerDropdown');
  };

  return (
    <div className="pt-0 mt-2 relative" ref={dropdownRef}>
      <div 
        className={`theme-bg-secondary bg-opacity-95 p-3 py-0 rounded-lg z-10 shadow-xs border theme-border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'} ${
          (isFullChartView || !isLeftPanelVisible) ? 'cursor-pointer hover:opacity-90 transition-opacity duration-200' : ''
        }`}
        onClick={() => {
          if (isFullChartView || !isLeftPanelVisible) {
            toggleDropdown('marketTickerDropdown');
          }
        }}
        data-dropdown
      >
        <div className="flex items-center space-x-2 justify-between">
          {/* Position P&L Section - Only show if there are open positions */}
          {openPositions.length > 0 && (
            <div className="flex items-center space-x-2 justify-start">
              <h2 className="text-xs font-medium theme-text-quaternary">
                PnL:
              </h2>
              <div className="flex items-center space-x-1">
                <span className={`text-xs font-medium ${
                  totalUnrealizedPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'
                }`}>
                  {totalUnrealizedPnL >= 0 ? '+' : ''}${totalUnrealizedPnL.toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2 justify-start">
            <h2 className="text-xs font-medium theme-text-quaternary">
              {selectedSymbol}
            </h2>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-[#2AB7A6] rounded-full animate-pulse"></div>
              <span className="text-xs text-[#2AB7A6]">LIVE</span>
            </div>
            {(isFullChartView || !isLeftPanelVisible) && (
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${
                isOpen('marketTickerDropdown') ? 'rotate-180' : ''
              } ${theme === 'light' ? 'text-gray-500' : 'theme-text-quaternary'}`} />
            )}
          </div>
          <div className="flex items-center space-x-2 justify-end">
            <div
              className={`w-30 text-right${!isSponsorEnabled ? ' text-xs' : ''} cursor-pointer hover:opacity-80 transition-opacity duration-200`}
              style={{ width: 80 }}
              onClick={(e) => {
                e.stopPropagation();
                const cleanPrice = currentPrice.replace(/[,$]/g, '');
                fillBuyPrice(cleanPrice);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                const cleanPrice = currentPrice.replace(/[,$]/g, '');
                fillSellPrice(cleanPrice);
              }}
              title="Click for Buy price | Right-click for Sell price"
            >
              <span className={`live-price-text font-bold theme-text-primary transition-all duration-300 ${
                !isSponsorEnabled ? 'text-sm' : ''
              }`}>
                <span
                  className={priceColor}
                  dangerouslySetInnerHTML={{ 
                    __html: formatPrice(parseFloat(currentPrice), selectedSymbol).replace(
                      /\$(\d+)/g, 
                      (match, digits) => `${parseInt(digits).toLocaleString()}`
                    )
                  }}
                />
              </span>
            </div>
            <div className="w-20--1">
              <span className={`text-sm font-medium flex items-center ${isPricePositive ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
                {isPricePositive ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                {priceChange && priceChange !== 'NaN%' ? priceChange : '+0.00%'}
              </span>
            </div>
          </div>
        </div>       
      </div>

      {/* Market Ticker Dropdown - Show when in full chart view OR when left panel is closed */}
      {(isFullChartView || !isLeftPanelVisible) && isOpen('marketTickerDropdown') && (
        <div 
          className="absolute top-full left-0 right-0 z-20 mt-1"
          data-dropdown
        >
          <div className={`theme-bg-secondary rounded-lg shadow-lg border theme-border ${
            theme === 'light' ? 'border-gray-200' : 'border-gray-700'
          }`}>
            <MarketTicker
              selectedMarket={selectedMarket}
              onMarketChange={setSelectedMarket}
              onSymbolSelect={handleSymbolSelect}
              selectedSymbol={selectedSymbol}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePriceRight;
